import { useTranslation } from "react-i18next";
import { ChevronLeft, UserIcon } from "lucide-react";
import { startCase } from "lodash";
import { Link, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/utils/cn";
import { formatPerson, normalizeStatus } from "@/utils/helper";

interface EntityUser {
  id?: number | string | null;
  profileImage?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  employmentDetails?: {
    employeeId?: string | number | null;
    department?: { name?: string | null } | null;
    position?: { title?: string | null } | null;
  } | null;
}

/** My-Space variant: shows a page title and back button. */
type SelfProps = {
  variant?: "self";
  title: string;
  user?: never;
  employeeId?: never;
};

/** Company/admin variant: shows employee avatar, name, and ID. */
type EntityProps = {
  variant: "entity";
  title?: never;
  user?: EntityUser | null;
  /** Direct override when employeeId is not nested under employmentDetails. */
  employeeId?: string | number | null;
};

type Props = (SelfProps | EntityProps) & {
  status?: string | null;
  onBack?: () => void;
  rightSlot?: ReactNode;
  className?: string;
};

/** Reusable header for request detail pages.
 *
 * variant="self"   → title text + back button (My Space pages)
 * variant="entity" → employee avatar + name + ID (Company pages)
 */
export const RequestDetailsHeader = ({
  title,
  user,
  status,
  employeeId: employeeIdProp,
  onBack,
  rightSlot,
  className,
}: Props) => {
  const { t } = useTranslation(["common", "company"]);
  const navigate = useNavigate();

  const fullName = formatPerson(user, "");
  const employeeId = employeeIdProp ?? user?.employmentDetails?.employeeId;
  const hasEmployeeData = Boolean(
    fullName || employeeId != null || user?.profileImage,
  );

  const employeeBlock = (
    <div className="flex items-center gap-3 min-w-0">
      <div className="relative shrink-0">
        <Avatar className="h-10 w-10 rounded-sm">
          <AvatarImage
            src={user?.profileImage || undefined}
            alt={fullName || t("common:labels.employee")}
          />
          <AvatarFallback className="rounded-sm bg-primary/10 text-primary text-sm font-semibold">
            {fullName?.charAt(0)?.toUpperCase() ?? (
              <UserIcon size={16} className="text-gray-500" />
            )}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-success ring-2 ring-white" />
      </div>
      <div className="flex flex-col leading-tight min-w-0">
        {fullName && (
          <span className="text-sm font-semibold text-gray-900 truncate">
            {fullName}
          </span>
        )}
        {employeeId != null && (
          <span className="text-xs text-primary underline underline-offset-2 truncate">
            {employeeId}
          </span>
        )}
      </div>
    </div>
  );

  const normalizedStatus = normalizeStatus(status);

  return (
    <header
      className={cn(
        "flex items-center justify-between gap-3 flex-wrap",
        "px-3 py-2 rounded-sm border border-border bg-background",
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          aria-label={t("company:shared.go_back")}
          onClick={onBack ?? (() => navigate(-1))}
          className={cn(
            "h-11 w-11 flex items-center justify-center shrink-0",
            "rounded-sxs border border-border bg-white transition-colors",
            "hover:bg-primary/10 hover:border-primary/10",
            "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30",
          )}
        >
          <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
        </button>

        {title && !hasEmployeeData && (
          <h2 className="text-base md:text-lg font-semibold text-gray-900 truncate">
            {title}
          </h2>
        )}

        {hasEmployeeData &&
          (user?.id != null ? (
            <Link
              to={`${ROUTES.HOME.EMPLOYEES.ONBOARDING}?id=${user.id}&hTab=personal-details`}
              className="min-w-0"
            >
              {employeeBlock}
            </Link>
          ) : (
            employeeBlock
          ))}
      </div>

      {rightSlot ??
        (normalizedStatus && (
          <Badge variant={statusToVariant(normalizedStatus)} dot={false}>
            {t(`common:status.${normalizedStatus}`, {
              defaultValue: startCase(status!),
            })}
          </Badge>
        ))}
    </header>
  );
};
